import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchConsultationsAsync,
  updateConsultationStatusAsync,
  updateConsultationLevelAsync,
  clearError,
  setFilters,
  clearFilters,
  setPage,
  type Consultation
} from '../store/slices/consultationsSlice';
import { fetchAdsAsync } from '../store/slices/adsSlice';
import LoadingSpinner from './LoadingSpinner';
import ConfirmationModal from './ConfirmationModal';
import StatusMeaningsModal from './StatusMeaningsModal';
import ConsultationLostReasonField from './ConsultationLostReasonField';
import Select, { SelectOption } from './Select';
import { STATUS_LABELS, allowedTargets, statusLabel, LOST_REASON_OTHER_MAXLEN, type ConsultationStatus } from '../constants/consultationStatus';
import { useLevels } from '../hooks/useLevels';
import { formatDateEuropean } from '../utils/dateUtils';
import './Students.css';
import './GroupForm.css';
import './Consultations.css';

const Consultations: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  const { consultations, loading, error, pagination, filters } = useAppSelector((state) => state.consultations);
  const { ads } = useAppSelector((state) => state.ads);
  const levels = useLevels();

  const [searchInput, setSearchInput] = useState(filters.search);
  const [adFilter, setAdFilter] = useState(filters.adId);
  const [statusFilter, setStatusFilter] = useState(filters.status);
  const [levelFilter, setLevelFilter] = useState(filters.langLevel);
  const [dateFromFilter, setDateFromFilter] = useState(filters.dateFrom);
  const [dateToFilter, setDateToFilter] = useState(filters.dateTo);
  const [hasRecordingFilter, setHasRecordingFilter] = useState(filters.hasRecording);
  const initialLoadRef = useRef(false);
  const defaultAdSetRef = useRef(false);
  const [statusHelpOpen, setStatusHelpOpen] = useState(false);
  // Lost-reason captured in the status-change modal when setting `not_interested`.
  const [modalLostReason, setModalLostReason] = useState('');
  const [modalLostReasonOther, setModalLostReasonOther] = useState('');

  // Modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'status' | 'level' | null;
    consultationId: number | null;
    newValue: string | null | undefined;
    currentValue: string | null | undefined;
  }>({
    isOpen: false,
    type: null,
    consultationId: null,
    newValue: null,
    currentValue: null,
  });

  // Load ads for the filter
  useEffect(() => {
    dispatch(fetchAdsAsync({ page: 1, limit: 1000 }));
  }, [dispatch]);

  // Filter active ads (memoized to avoid unnecessary recalculations)
  const activeAds = useMemo(() => {
    return ads.filter(ad => {
      const endDate = new Date(ad.dateEnd);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return endDate >= today;
    });
  }, [ads]);

  // Set default ad filter if there's exactly one active ad
  useEffect(() => {
    if (!defaultAdSetRef.current && activeAds.length === 1) {
      const defaultAdId = activeAds[0].id.toString();
      setAdFilter(defaultAdId);
      dispatch(setFilters({ adId: defaultAdId }));
      defaultAdSetRef.current = true;
    }
  }, [activeAds, dispatch]);

  // Load initial data
  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
    }
  }, [dispatch]);

  // Load consultations data when filters change
  const loadConsultations = useCallback(() => {
    dispatch(fetchConsultationsAsync({
      page: pagination.currentPage,
      limit: pagination.itemsPerPage,
      search: filters.search,
      adId: filters.adId,
      status: filters.status,
      langLevel: filters.langLevel,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      hasRecording: filters.hasRecording,
    }));
  }, [dispatch, pagination.currentPage, pagination.itemsPerPage, filters]);

  useEffect(() => {
    loadConsultations();
  }, [loadConsultations]);

  const handleSearch = useCallback(() => {
    dispatch(setFilters({ 
      search: searchInput.trim(),
      adId: adFilter,
      status: statusFilter,
      langLevel: levelFilter,
      dateFrom: dateFromFilter,
      dateTo: dateToFilter,
      hasRecording: hasRecordingFilter,
    }));
  }, [dispatch, searchInput, adFilter, statusFilter, levelFilter, dateFromFilter, dateToFilter, hasRecordingFilter]);

  const handleClearFilters = useCallback(() => {
    setSearchInput('');
    setAdFilter('all');
    setStatusFilter('all');
    setLevelFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
    setHasRecordingFilter('all');
    dispatch(clearFilters());
    defaultAdSetRef.current = false;
  }, [dispatch]);

  const handlePageChange = useCallback((page: number) => {
    dispatch(setPage(page));
  }, [dispatch]);

  const formatDate = useCallback((dateString: string) => {
    return formatDateEuropean(dateString);
  }, []);

  const getFullName = (consultation: Consultation) => {
    const parts = [consultation.lastName, consultation.firstName, consultation.patronymic].filter(Boolean);
    return parts.join(' ') || '-';
  };

  const handleStatusChange = (consultationId: number, currentStatus: string | null | undefined, newStatus: string) => {
    setModalLostReason('');
    setModalLostReasonOther('');
    setConfirmModal({
      isOpen: true,
      type: 'status',
      consultationId,
      newValue: newStatus,
      currentValue: currentStatus ?? null,
    });
  };

  const handleLevelChange = (consultationId: number, currentLevel: string | null | undefined, newLevel: string) => {
    setConfirmModal({
      isOpen: true,
      type: 'level',
      consultationId,
      newValue: newLevel,
      currentValue: currentLevel ?? null,
    });
  };

  const handleConfirmChange = async () => {
    if (!confirmModal.consultationId || !confirmModal.type) return;

    try {
      if (confirmModal.type === 'status') {
        const isNotInterested = confirmModal.newValue === 'not_interested';
        await dispatch(updateConsultationStatusAsync({
          id: confirmModal.consultationId,
          status: (confirmModal.newValue === 'none' ? null : confirmModal.newValue) as ConsultationStatus,
          lostReason: isNotInterested ? (modalLostReason || null) : null,
          lostReasonOther: isNotInterested && modalLostReason === 'other' ? (modalLostReasonOther.trim() || null) : null,
        })).unwrap();
      } else if (confirmModal.type === 'level') {
        await dispatch(updateConsultationLevelAsync({
          id: confirmModal.consultationId,
          langLevel: confirmModal.newValue === 'none' ? null : (confirmModal.newValue ?? null)
        })).unwrap();
      }
    } catch (error) {
      console.error('Failed to update:', error);
    }

    setConfirmModal({
      isOpen: false,
      type: null,
      consultationId: null,
      newValue: null,
      currentValue: null,
    });
  };

  const handleCancelChange = () => {
    setConfirmModal({
      isOpen: false,
      type: null,
      consultationId: null,
      newValue: null,
      currentValue: null,
    });
  };


  const getModalMessage = () => {
    if (confirmModal.type === 'status') {
      const currentLabel = statusLabel(confirmModal.currentValue as ConsultationStatus | null);
      const newLabel = statusLabel(confirmModal.newValue as ConsultationStatus | null);
      return `Change status from "${currentLabel}" to "${newLabel}"?`;
    } else if (confirmModal.type === 'level') {
      const currentLabel = confirmModal.currentValue ? confirmModal.currentValue.toUpperCase() : 'Unknown';
      const newLabel = confirmModal.newValue === 'none' ? 'Unknown' : (confirmModal.newValue as string).toUpperCase();
      return `Change language level from "${currentLabel}" to "${newLabel}"?`;
    }
    return '';
  };

  if (loading && consultations.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Consultations</h1>
          <p>Manage consultation records</p>
        </div>
        <button 
          onClick={() => navigate('/consultations/create')}
          className="btn-primary"
        >
          + Create Consultation
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="filter-bar">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by name..."
          className="search-input"
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />
        <select value={adFilter} onChange={(e) => setAdFilter(e.target.value)} className="form-select">
          <option value="all">All Sources</option>
          <option value="referral">Referral</option>
          <option value="organic">Organic</option>
          {ads.map(ad => <option key={ad.id} value={ad.id}>{ad.name}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="form-select">
          <option value="all">All Statuses</option>
          <option value="scheduled">Scheduled</option>
          <option value="awaiting_payment">Awaiting Payment</option>
          <option value="waitlist">Waitlist</option>
          <option value="paid">Paid</option>
          <option value="not_interested">Not Interested</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)} className="form-select">
          <option value="all">All Levels</option>
          <option value="none">Unknown</option>
          {levels.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
        <input type="date" value={dateFromFilter} onChange={(e) => setDateFromFilter(e.target.value)} className="form-input" />
        <span className="filter-sep">-</span>
        <input type="date" value={dateToFilter} onChange={(e) => setDateToFilter(e.target.value)} className="form-input" />
        <select value={hasRecordingFilter} onChange={(e) => setHasRecordingFilter(e.target.value)} className="form-select">
          <option value="all">Recordings</option>
          <option value="true">With</option>
          <option value="false">Without</option>
        </select>
        <button onClick={handleSearch} className="btn-primary">Apply</button>
        <button onClick={handleClearFilters} className="btn-secondary">Clear</button>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <p>
          Showing {consultations.length} of {pagination.totalItems} consultations
          {(filters.search || filters.adId !== 'all' || filters.status !== 'all' || filters.langLevel !== 'all' || filters.dateFrom || filters.dateTo || filters.hasRecording !== 'all') && ' (filtered)'}
        </p>
      </div>

      {/* Consultations Table */}
      <div className="students-table-container vocabulary-table">
        {consultations.length === 0 ? (
          <div className="no-students">
            <p>No consultations found.</p>
            {filters.search && (
              <button onClick={handleClearFilters} className="btn-primary">
                Clear filters to see all consultations
              </button>
            )}
          </div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Source</th>
                <th>Contact</th>
                <th>Consultation Date</th>
                <th>
                  Status
                  <button
                    type="button"
                    className="status-help-icon"
                    onClick={() => setStatusHelpOpen(true)}
                    title="What do the statuses mean?"
                    aria-label="Status meanings"
                  >
                    ?
                  </button>
                </th>
                <th>Level</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {consultations.map((consultation) => {
                const hasRecording = !!consultation.recordingId;
                const recStatus = consultation.recordingStatus;
                return (
                <tr key={consultation.id} className={hasRecording ? 'consultations-row--has-recording' : ''}>
                  <td>
                    <strong>{getFullName(consultation)}</strong>
                    {hasRecording && (
                      <span
                        className={`recording-pill recording-pill--${recStatus || 'unknown'}`}
                        title={`Recording status: ${recStatus || 'unknown'}`}
                      >
                        🎙 {recStatus === 'completed' ? 'Recorded' :
                            recStatus === 'failed'    ? 'Failed'   :
                            recStatus === 'processing' ? 'Transcribing' :
                            recStatus === 'pending'   ? 'Queued'   :
                            'Recorded'}
                      </span>
                    )}
                    {hasRecording && consultation.recordingHealthy === false && (
                      <span
                        className="recording-pill recording-pill--unhealthy"
                        title="Client audio leaked into the teacher track — transcript may be unreliable"
                      >
                        ⚠ Audio leak
                      </span>
                    )}
                  </td>
                  <td>
                    {consultation.referrerStudentId ? (
                      <span style={{ color: '#667eea', fontWeight: 500 }}>Referral</span>
                    ) : consultation.isOrganic ? (
                      <span style={{ color: '#10b981', fontWeight: 500 }}>Organic</span>
                    ) : (
                      consultation.ad?.name || '-'
                    )}
                  </td>
                  <td>
                    {consultation.contact}
                  </td>
                  <td>
                    {formatDate(consultation.consultationDate)}
                  </td>
                  <td>
                    <div className="inline-status-select">
                      <Select<ConsultationStatus, SelectOption<ConsultationStatus>>
                        value={consultation.status ? { value: consultation.status, label: STATUS_LABELS[consultation.status] } : null}
                        onChange={(opt) => opt && handleStatusChange(consultation.id, consultation.status, opt.value)}
                        options={allowedTargets(consultation.status).map(s => ({ value: s, label: STATUS_LABELS[s] }))}
                        placeholder="Set status…"
                        isClearable={false}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="inline-status-select">
                      <Select<string, SelectOption<string>>
                        value={
                          consultation.langLevel
                            ? levels.find(l => l.value === consultation.langLevel) ||
                              { value: consultation.langLevel, label: consultation.langLevel.toUpperCase() }
                            : { value: 'none', label: 'Unknown' }
                        }
                        onChange={(opt) => opt && handleLevelChange(consultation.id, consultation.langLevel, opt.value)}
                        options={[{ value: 'none', label: 'Unknown' }, ...levels]}
                        placeholder="Set level…"
                        isClearable={false}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => navigate(`/consultations/edit/${consultation.id}`)}
                        className="btn-icon"
                        data-tooltip="Edit consultation"
                      >
                        ✎
                      </button>
                      {hasRecording ? (
                        <button
                          onClick={() => navigate(`/recordings/${consultation.recordingId}`)}
                          className="btn-icon btn-icon--recording"
                          data-tooltip="View recording"
                        >
                          🎙
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate(`/consultations/${consultation.id}/recordings/new`)}
                          className="btn-icon btn-icon--upload"
                          data-tooltip="Upload recording"
                        >
                          <span className="btn-icon__mic">🎙</span>
                          <span className="btn-icon__add" aria-hidden>+</span>
                        </button>
                      )}
                      {['awaiting_payment', 'waitlist', 'paid'].includes(consultation.status || '') && !consultation.studentId && (
                        <button
                          onClick={() => navigate(`/students/create?fromConsultation=${consultation.id}`)}
                          className="btn-icon"
                          data-tooltip="Create student"
                          style={{ color: '#059669' }}
                        >
                          +👤
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1 || loading}
            className="btn-secondary"
          >
            Previous
          </button>
          
          <div className="pagination-info">
            <span>
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
          </div>
          
          <button 
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages || loading}
            className="btn-secondary"
          >
            Next
          </button>
        </div>
      )}

      {loading && consultations.length > 0 && (
        <div className="loading-overlay">
          <LoadingSpinner />
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title="Confirm Change"
        message={getModalMessage()}
        onConfirm={handleConfirmChange}
        onCancel={handleCancelChange}
        confirmText="Confirm"
        cancelText="Cancel"
        confirmDisabled={
          confirmModal.type === 'status' &&
          confirmModal.newValue === 'not_interested' &&
          modalLostReason === 'other' &&
          !modalLostReasonOther.trim()
        }
      >
        {confirmModal.type === 'status' && confirmModal.newValue === 'not_interested' && (
          <div style={{ marginTop: 12 }}>
            <ConsultationLostReasonField
              lostReason={modalLostReason}
              lostReasonOther={modalLostReasonOther}
              noteError={
                modalLostReason === 'other' && !modalLostReasonOther.trim()
                  ? `A note is required (max ${LOST_REASON_OTHER_MAXLEN} chars)`
                  : undefined
              }
              onChange={({ lostReason, lostReasonOther }) => {
                setModalLostReason(lostReason);
                setModalLostReasonOther(lostReasonOther);
              }}
            />
          </div>
        )}
      </ConfirmationModal>

      <StatusMeaningsModal isOpen={statusHelpOpen} onClose={() => setStatusHelpOpen(false)} />
    </div>
  );
};

export default Consultations;
